import type { Price } from '@/scopes/prices/domain/prices/types'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPricesViewedClickEvent } from '@/scopes/prices/domain/analytics'
import styles from './PriceCell.module.css'
import * as Table from './Table'

export const PriceCell = React.memo(
  ({
    price,
    onClick,
    isSelected,
  }: {
    price: Price | undefined
    onClick: (priceId: string) => void
    isSelected: boolean
  }) => {
    const intl = useIntl()

    const { sendEvent } = useAnalytics()

    const id = price?.id ?? ''
    const duration = price?.duration?.toString() ?? ''

    const onClickHandler = React.useCallback(() => {
      if (price) {
        sendEvent(
          createPricesViewedClickEvent({
            price: price.averagePrice,
            isHighlighted: price.isBestValue,
          }),
        )
        onClick(id)
      }
    }, [price, sendEvent, onClick, id])

    const displayPrice = price?.averagePrice
    const priceText = displayPrice ? intl.formatTruncatedCurrency(Math.ceil(displayPrice)) : '-'
    const hasPrice = Boolean(displayPrice)
    const selected = isSelected && hasPrice
    const isBestValue = price?.isBestValue ?? false

    return (
      <Table.Cell
        as="button"
        className={cn(styles.priceCell, {
          [styles.priceCellHasPrice]: hasPrice,
          [styles.priceCellNoPrice]: !hasPrice,
          [styles.priceCellHoverable]: !selected && hasPrice && !isBestValue,
          [styles.priceCellBestValue]: isBestValue,
          [styles.priceCellSelected]: selected,
          [styles.priceCellSelectedBestValue]: selected && isBestValue,
        })}
        onClick={onClickHandler}
        data-qa-price-value={priceText}
        role="gridcell"
        tabIndex={hasPrice ? 0 : -1}
        type="button"
      >
        {price?.bulletIcon && (
          <Icon
            className={cn(styles.bulletIcon, price?.isBestValue && styles.bulletIconToBack)}
            size={16}
            glyph={price.bulletIcon}
          />
        )}
        {price?.isBestValue && <Icon className={styles.bulletIcon} glyph="ArrowDownInCircleMulticolor" size={16} />}
        <Typography className={!hasPrice ? styles.priceTextNoPrice : undefined} variant="body-l-bold-flat">{priceText}</Typography>
        <Typography className={!hasPrice ? styles.priceTextNoPrice : undefined} variant="body-xs-regular-flat">{duration ? intl.formatMessage({ id: 'prices_grid_duration_header' }, { n_days: duration }) : ''}</Typography>
      </Table.Cell>
    )
  },
)

PriceCell.displayName = 'PriceCellByWeeks'
